import { Suspense, lazy } from 'react';
import type { SlotEntry, SlotProps } from '@saas/ui';

/**
 * 画面の差し込み口（`@saas/ui` の `Slot`）に knowledge が出すもの（n-contracts.md §1）。登録は apps/web（リード）が行う。
 *
 * ★ 部品は `lazy` + `Suspense fallback={null}`（初期バンドルを太らせない）。
 * ★ どれも**中身が空なら null を返して場所を取らない**。API の 403 / 404 は「空」として扱う（knowledge が無効なテナント）。
 * ★ 出すかどうか（モジュールの有効 / 権限）は Slot 側が見る（`permission`）。
 *
 * | スロット | 出すもの | subjectType | context のキー |
 * |---|---|---|---|
 * | `form.assist` | 入力中の本文から「似た記録」3 件（§14.5） | workflow_request / deal / report / project | `text`（必須）・`customerId` |
 * | `form.assist` | 見込み客の「似た問い合わせへの過去の対応」3 件（SFA §5.10） | lead | `text`（問い合わせの種類の名前など・任意）・`customerId`。原文は送らない |
 * | `record.panel` | 「このレコードについて社内が知っていること」5 件 | customer / contact / deal / project / contract | `customerId`（分かるとき） |
 * | `meeting.afterSummary` | 会議後の 1 問（§15.2 ①） | meeting | `customerId`・`title` |
 */
const FormAssistPanel = lazy(() => import('./components/FormAssistPanel.js'));
const RecordKnowledgePanel = lazy(() => import('./components/RecordKnowledgePanel.js'));
const AfterMeetingNote = lazy(() => import('./components/AfterMeetingNote.js'));

function wrap(Component: React.ComponentType<SlotProps>): React.ComponentType<SlotProps> {
  return (props: SlotProps) => (
    <Suspense fallback={null}>
      <Component {...props} />
    </Suspense>
  );
}

export const knowledgeSlots: SlotEntry[] = [
  {
    // 起票・見積・日報・案件の作成の横（§14.5「日常で一番効く」）。入力が止まってから 800ms・3 件まで。
    // 見込み客の詳細（lead・SFA §5.10）では「似た問い合わせへの過去の対応」。問い合わせの原文はサーバが crm から読む（画面からは送らない）
    id: 'knowledge.form-assist',
    module: 'knowledge',
    slot: 'form.assist',
    subjectTypes: ['workflow_request', 'deal', 'report', 'project', 'lead'],
    permission: ['knowledge.search'],
    order: 20,
    Component: wrap(FormAssistPanel),
  },
  {
    // レコードの詳細（§14.5）。refs で引いた上位 5 件 + 「探す」への導線
    id: 'knowledge.record-panel',
    module: 'knowledge',
    slot: 'record.panel',
    subjectTypes: ['customer', 'contact', 'deal', 'project', 'contract'],
    permission: ['knowledge.search'],
    order: 40,
    Component: wrap(RecordKnowledgePanel),
  },
  {
    // 会議の要約を確定した直後の 1 問（§15.2 ①）。必須にしない・「今後聞かない」を必ず出す
    id: 'knowledge.after-meeting',
    module: 'knowledge',
    slot: 'meeting.afterSummary',
    subjectTypes: ['meeting'],
    permission: ['knowledge.note.own'],
    order: 10,
    Component: wrap(AfterMeetingNote),
  },
];
